import { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Video, Loader2, X, Upload, Link as LinkIcon, FileVideo,
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadViaPresignedUrl, uploadViaChunks } from './videoUploadHelpers';
import { VideoPreview } from './VideoPreview';
import { ThumbnailSection } from './ThumbnailSection';

const API = process.env.REACT_APP_BACKEND_URL;

const isUploadedVideo = (url) => url && (url.startsWith('/api/uploads/videos/') || url.includes('.amazonaws.com/'));

export const VideoUploadSection = ({ workout, updateField, token, isNew }) => {
  const [videoMode, setVideoMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  const abortRef = useRef(null);

  const cancelUpload = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    toast.info('Upload cancelled');
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['mp4', 'webm', 'mov', 'avi', 'mpeg', 'mpg', 'm4v', '3gp', 'mkv'];
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !allowedExtensions.includes(fileExt)) {
      toast.error('Invalid file type. Please upload a video file.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    abortRef.current = null;
    const sizeMB = Math.round(file.size / 1048576);
    toast.info(`Starting upload: ${file.name} (${sizeMB} MB)`);

    const apiBase = API || window.location.origin;
    const workoutId = (!isNew && workout.id) ? workout.id : null;

    try {
      let data;
      try {
        data = await uploadViaPresignedUrl({ apiBase, token, file, workoutId, onProgress: setUploadProgress, abortRef });
      } catch (presignErr) {
        toast.info('Switching to chunked upload...');
        setUploadProgress(0);
        data = await uploadViaChunks({ apiBase, token, file, workoutId, onProgress: setUploadProgress, abortRef });
      }
      updateField('video_url', data.video_url);
      updateField('video_type', 'uploaded');
      toast.success(`Video uploaded! (${data.size_mb || sizeMB} MB)`);
    } catch (err) {
      if (err.message !== 'Upload cancelled') {
        toast.error(err.message || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      abortRef.current = null;
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  return (
    <div className="md:col-span-2 space-y-4" data-testid="video-upload-section">
      <div className="flex items-center justify-between">
        <Label className="text-white uppercase tracking-wider text-sm">
          <Video className="inline h-4 w-4 mr-2" /> Workout Video
        </Label>
        <div className="flex gap-2">
          <Button
            type="button" variant={videoMode === 'url' ? 'default' : 'outline'} size="sm"
            onClick={() => setVideoMode('url')}
            className={videoMode === 'url' ? 'bg-primary text-black' : 'border-border text-white'}
            data-testid="video-mode-url"
          >
            <LinkIcon className="h-4 w-4 mr-1" /> YouTube URL
          </Button>
          <Button
            type="button" variant={videoMode === 'upload' ? 'default' : 'outline'} size="sm"
            onClick={() => setVideoMode('upload')}
            className={videoMode === 'upload' ? 'bg-primary text-black' : 'border-border text-white'}
            data-testid="video-mode-upload"
          >
            <Upload className="h-4 w-4 mr-1" /> Upload Video
          </Button>
        </div>
      </div>

      {videoMode === 'url' ? (
        <div>
          <Input
            value={isUploadedVideo(workout.video_url) ? '' : (workout.video_url || '')}
            onChange={(e) => {
              updateField('video_url', e.target.value);
              updateField('video_type', 'youtube');
            }}
            className="bg-black border-border text-white"
            placeholder="https://www.youtube.com/watch?v=... or https://www.youtube.com/embed/..."
            data-testid="workout-video-input"
          />
          <p className="text-xs text-muted-foreground mt-1">Paste a YouTube video URL or embed URL</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              uploading ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 cursor-pointer'
            }`}
            onClick={() => !uploading && videoInputRef.current?.click()}
            data-testid="video-upload-dropzone"
          >
            {uploading ? (
              <div className="space-y-3">
                <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto" />
                <p className="text-white">Uploading video... {uploadProgress}%</p>
                <div className="h-2 bg-black/50 rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={(e) => { e.stopPropagation(); cancelUpload(); }}
                  className="text-red-400 hover:text-red-300 mt-2"
                  data-testid="cancel-upload-btn"
                >
                  <X className="h-4 w-4 mr-1" /> Cancel Upload
                </Button>
              </div>
            ) : (
              <>
                <FileVideo className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-white font-medium">Click to upload a video</p>
                <p className="text-xs text-muted-foreground mt-1">MP4, WebM, MOV, AVI and more (no size limit)</p>
              </>
            )}
          </div>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
            data-testid="video-upload-input"
          />
        </div>
      )}

      {workout.video_url && (
        <VideoPreview
          videoUrl={workout.video_url}
          onRemove={() => { updateField('video_url', ''); updateField('video_type', null); }}
        />
      )}

      {workout.video_url && (
        <ThumbnailSection workout={workout} updateField={updateField} token={token} />
      )}
    </div>
  );
};
